import { HttpContext, HttpHeaders, HttpParams } from '@angular/common/http';
import { inject, Inject, Injectable, Injector, untracked } from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';

import { Observable, distinctUntilChanged, filter, switchMap, take } from 'rxjs';
import { OrganizationsStore } from '@shared/state';
import { BRCApiService } from '../brc-api/brc-api.service';
import { isEqual } from 'lodash';
import { HRT_BASE_URL, HRT_SUBSCRIPTION_KEY } from '@shared/utils/tokens';

// from httpClient's get method
type HttpOptions = {
  headers?:
    | HttpHeaders
    | {
        [header: string]: string | string[];
      };
  context?: HttpContext;
  observe?: 'body';
  params?:
    | HttpParams
    | {
        [param: string]: string | number | boolean | Readonly<string | number | boolean>[];
      };
  reportProgress?: boolean;
  responseType?: 'json';
  withCredentials?: boolean;
};

/**
 * use this service instead of the httpClient to make sure
 * some basic headers are already taken care of.
 */
@Injectable({
  providedIn: 'root',
})
export class HrtApiService extends BRCApiService {
  private readonly injector = inject(Injector);

  private organizationIdStream?: Observable<number>;

  /**
   * Resolved lazily: `OrganizationsStore` transitively depends on this service through the
   * feature API services, so eager injection would create a circular dependency.
   * The bridge is created once and outside any reactive context, so callers may read it
   * from within effects and computeds without creating a new effect per request.
   */
  get organizationId$(): Observable<number> {
    this.organizationIdStream ??= untracked(() =>
      toObservable(this.injector.get(OrganizationsStore).organizationId, { injector: this.injector }).pipe(
        filter((id): id is number => Boolean(id)),
        distinctUntilChanged(isEqual)
      )
    );

    return this.organizationIdStream;
  }

  constructor(
    @Inject(HRT_BASE_URL) protected hrtBaseUrl: string,
    @Inject(HRT_SUBSCRIPTION_KEY) protected subscriptionKey: string
  ) {
    super(hrtBaseUrl, subscriptionKey);
  }

  /**
   * a wrapper for the httpClient's GET method
   * - sets an organizationId in front of the url
   * @param url the part of the url after the hrtBaseUrl
   * @param options object containing extra HTTP Headers or params
   */
  getForOrganization<T>(url: string, options: HttpOptions = {}): Observable<T> {
    return this.forOrganization<T>(url, (organizationUrl) => this.get<T>(organizationUrl, options));
  }

  /**
   * a wrapper for the httpClient's PUT method
   * - sets an organizationId in front of the url
   * @param url the part of the url after the hrtBaseUrl
   * @param payload body of the request
   * @param options object containing extra HTTP Headers or params
   */
  putForOrganization<T>(url: string, payload?: unknown, options: HttpOptions = {}): Observable<T> {
    return this.forOrganization<T>(url, (organizationUrl) => this.put<T>(organizationUrl, payload, options));
  }
  /**
   * a wrapper for the httpClient's PATCH method
   * - sets an organizationId in front of the url
   * @param url the part of the url after the hrtBaseUrl
   * @param payload body of the request
   * @param options object containing extra HTTP Headers or params
   */
  patchForOrganization<T>(url: string, payload?: unknown, options: HttpOptions = {}): Observable<T> {
    return this.forOrganization<T>(url, (organizationUrl) =>
      this.patch<T>(organizationUrl, { ...(payload as object), id: undefined }, options)
    );
  }

  /**
   * a wrapper for the httpClient's POST method
   * - sets an organizationId in front of the url
   * @param url the part of the url after the hrtBaseUrl
   * @param payload body of the request
   * @param options object containing extra HTTP Headers or params
   */
  postForOrganization<T>(url: string, payload?: unknown, options: HttpOptions = {}): Observable<T> {
    return this.forOrganization<T>(url, (organizationUrl) => this.post<T>(organizationUrl, payload, options));
  }

  private forOrganization<T>(url: string, request: (organizationUrl: string) => Observable<T>): Observable<T> {
    return this.organizationId$.pipe(
      take(1),
      switchMap((id) => request(`organizations/${id}/${url}`))
    );
  }
}
